import * as R from 'ramda';

import {
  CheckCircleIcon,
  type IconComponent,
  WarningIcon,
} from '@atom/mui/icons';
import { AssetDetail } from '@atom/types/asset';
import {
  ARDOTAttribute,
  AssetRequestAttribute,
  AssetRequestCreateInput,
  AssetRequestsFilters,
  AssetRequestStatus,
  AssetRequestType,
  Column,
  ConfirmModalContent,
} from '@atom/types/assetRequest';
import { InventorySchemaItem, Schema, SchemasInput } from '@atom/types/schema';
import { Client } from '@atom/utilities/featureToggleUtilities';
import { assetRequestClient } from '@atom/utilities/tenantFeatureUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import { getADOTTypeChangeFilters } from './adotFilters/adotFilterUtilities';
import { toADOTBaseCreateInputs } from './adotReplenishmentUtilities';
import {
  ADOT_CONFIRM_MODAL_CONTENT,
  ADOT_INITIAL_SORT,
  ADOT_REQUEST_TYPES,
  adotColumns,
  ARDOT_CONFIRM_MODAL_CONTENT,
  ardotColumns,
  DEFAULT_INITIAL_SORT,
  InitialSort,
  INVENTORY_STATUS_OPTIONS,
  REPLENISHMENT_STATUS_OPTIONS,
  SDDOT_CONFIRM_MODAL_CONTENT,
  SDDOT_FUEL_SCHEMA_ID,
  SDDOT_INVENTORY_SCHEMA_ID,
  SDDOT_STOCKPILE_SCHEMA_ID,
  sddotColumns,
  TYPE_LABELS,
} from './assetRequestConstants';

interface CustomValues {
  initialFilters: AssetRequestsFilters;
  initialSort: InitialSort;
  typeOptions: AssetRequestType[];
}

// INITIAL FILTERS
const getSDDOTInitialFilters = (
  schema?: InventorySchemaItem | Schema,
  asset?: AssetDetail,
): AssetRequestsFilters => {
  if (isNilOrEmpty(schema) && isNilOrEmpty(asset)) {
    return {
      type: AssetRequestType.SDDOT_INVENTORY_TRANSFER,
      schemaIds: [SDDOT_INVENTORY_SCHEMA_ID],
    };
  }

  const type =
    schema?.id === SDDOT_STOCKPILE_SCHEMA_ID
      ? AssetRequestType.SDDOT_STOCKPILE_TRANSFER
      : AssetRequestType.SDDOT_INVENTORY_TRANSFER;

  const addAssetFilters =
    type === AssetRequestType.SDDOT_INVENTORY_TRANSFER ||
    type === AssetRequestType.SDDOT_STOCKPILE_TRANSFER;

  return {
    type,
    assetId: asset?.id,
    ...(addAssetFilters && {
      fromAssetId: asset?.id,
    }),
  };
};

const getARDOTInitialFilters = (
  schema?: InventorySchemaItem | Schema,
  asset?: AssetDetail,
): AssetRequestsFilters => {
  if (isNilOrEmpty(schema) && isNilOrEmpty(asset)) {
    return {
      type: AssetRequestType.ARDOT_118_TRANSFER,
    };
  }

  return {
    type: AssetRequestType.ARDOT_118_TRANSFER,
    assetId: asset?.id,
  };
};

const getADOTInitialFilters = (
  schema?: InventorySchemaItem | Schema,
  asset?: AssetDetail,
): AssetRequestsFilters => {
  if (isNilOrEmpty(schema) && isNilOrEmpty(asset)) {
    return {
      type: AssetRequestType.ADOT_INVENTORY_TRANSFER,
    };
  }

  return {
    type: AssetRequestType.ADOT_INVENTORY_TRANSFER,
    assetId: asset?.id,
    fromAssetId: asset?.id,
  };
};

// TYPE OPTIONS
const getSDDOTTypeOptions = (
  schema: InventorySchemaItem | Schema,
): AssetRequestType[] => {
  if (isNilOrEmpty(schema)) {
    return [
      AssetRequestType.SDDOT_INVENTORY_TRANSFER,
      AssetRequestType.SDDOT_STOCKPILE_TRANSFER,
      AssetRequestType.SDDOT_REPLENISHMENT,
      AssetRequestType.SDDOT_ADJUSTMENT,
    ];
  }

  return [
    ...[
      schema?.id === SDDOT_STOCKPILE_SCHEMA_ID
        ? AssetRequestType.SDDOT_STOCKPILE_TRANSFER
        : AssetRequestType.SDDOT_INVENTORY_TRANSFER,
    ],
    AssetRequestType.SDDOT_REPLENISHMENT,
    AssetRequestType.SDDOT_ADJUSTMENT,
  ];
};

// STATUS OPTIONS
export const getSDDOTStatusOptions = (
  type: AssetRequestType,
): AssetRequestStatus[] => {
  const statusOptions = {
    [AssetRequestType.SDDOT_REPLENISHMENT]: REPLENISHMENT_STATUS_OPTIONS,
    [AssetRequestType.SDDOT_INVENTORY_TRANSFER]: INVENTORY_STATUS_OPTIONS,
  };

  return statusOptions[type] || [];
};

// FULL CUSTOM VALUES
export const getCustomValues = (
  schema?: InventorySchemaItem | Schema,
  asset?: AssetDetail,
): CustomValues => {
  switch (assetRequestClient) {
    case Client.SDDOT:
      return {
        initialFilters: getSDDOTInitialFilters(schema, asset),
        initialSort: DEFAULT_INITIAL_SORT,
        typeOptions: getSDDOTTypeOptions(schema),
      };
    case Client.ARDOT:
      return {
        initialFilters: getARDOTInitialFilters(schema, asset),
        initialSort: DEFAULT_INITIAL_SORT,
        typeOptions: [],
      };
    case Client.ADOT:
      return {
        initialFilters: getADOTInitialFilters(schema, asset),
        initialSort: ADOT_INITIAL_SORT,
        typeOptions: ADOT_REQUEST_TYPES,
      };
    default:
      return {
        initialFilters: {},
        initialSort: DEFAULT_INITIAL_SORT,
        typeOptions: [],
      };
  }
};

export const getSchemaFilterInput = (): Partial<SchemasInput> => {
  switch (assetRequestClient) {
    case Client.SDDOT:
      return {
        ids: [
          SDDOT_FUEL_SCHEMA_ID,
          SDDOT_INVENTORY_SCHEMA_ID,
          SDDOT_STOCKPILE_SCHEMA_ID,
        ],
      };
    case Client.ARDOT:
      return { isMaterial: 'true' };
    case Client.ADOT:
      return { rootSchemas: true, onlyStockBased: true };
    default:
      return {};
  }
};

export type CategoryFilterInput = {
  schemaIds?: string[];
  onlyStockBased?: boolean;
};

export const getCategoryFilterInput = (): CategoryFilterInput => {
  switch (assetRequestClient) {
    case Client.SDDOT:
      return { schemaIds: getSchemaFilterInput().ids };
    case Client.ADOT:
      return { onlyStockBased: true };
    default:
      return {};
  }
};

// This method will adjust filters before they are applied with tenant specific logic
export const cleanFilters = (
  filters: AssetRequestsFilters,
): AssetRequestsFilters => {
  switch (assetRequestClient) {
    case Client.ARDOT:
    case Client.SDDOT:
    case Client.ADOT:
      return {
        ...R.reject(isNilOrEmpty, {
          ...filters,
        }),
        // Name filter only allows user to enter the sequence suffix, so we must
        // hydrate the type prefix here.
        ...(filters.name && {
          name: `${TYPE_LABELS[filters.type]} - ${filters.name}`,
        }),
      };
    default:
      return filters;
  }
};

// Gets filters for when the request type is changed
export const getSDDOTTypeChangeFilters = (
  type: AssetRequestType,
  asset?: AssetDetail,
): AssetRequestsFilters => {
  if (isNilOrEmpty(asset)) {
    return { type };
  }

  const updateAssetFilters =
    type === AssetRequestType.SDDOT_INVENTORY_TRANSFER ||
    type === AssetRequestType.SDDOT_STOCKPILE_TRANSFER;

  return {
    type,
    assetId: asset?.id,
    ...(updateAssetFilters && {
      fromAssetId: asset?.id,
    }),
  };
};

export const getTypeChangeFilters = (
  type: AssetRequestType,
  asset?: AssetDetail,
): AssetRequestsFilters => {
  switch (assetRequestClient) {
    case Client.SDDOT:
      return getSDDOTTypeChangeFilters(type, asset);
    case Client.ADOT:
      return getADOTTypeChangeFilters(type, asset);
    default:
      return {
        type,
        ...(!isNilOrEmpty(asset) && { assetId: asset?.id }),
      };
  }
};

export const getSDDOTColumns = (
  type: AssetRequestType,
  isPortalView: boolean,
): Column[] => {
  return sddotColumns(type, isPortalView);
};

export const getARDOTColumns = (isPortalView: boolean): Column[] => {
  return ardotColumns(isPortalView);
};

export const getTableColumns = (
  type: AssetRequestType,
  isPortalView: boolean,
) => {
  switch (assetRequestClient) {
    case Client.SDDOT:
      return getSDDOTColumns(type, isPortalView);
    case Client.ARDOT:
      return getARDOTColumns(isPortalView);
    case Client.ADOT:
      return adotColumns(type, isPortalView);
    default:
      return [];
  }
};

const defaultModalContent = { title: '', content: '' };

export const getSDDOTConfirmModalContent = (
  type: AssetRequestType,
): ConfirmModalContent => {
  return SDDOT_CONFIRM_MODAL_CONTENT[type] || defaultModalContent;
};

export const getConfirmModalContent = (
  type: AssetRequestType,
): ConfirmModalContent => {
  switch (assetRequestClient) {
    case Client.SDDOT:
      return getSDDOTConfirmModalContent(type);
    case Client.ARDOT:
      return ARDOT_CONFIRM_MODAL_CONTENT;
    case Client.ADOT:
      return ADOT_CONFIRM_MODAL_CONTENT[type] || defaultModalContent;
    default:
      return defaultModalContent;
  }
};

export const getReplenishmentSubtextOptions = (
  totalRemaining: number,
  assetUnit: string,
): { icon: IconComponent; message: string } => {
  switch (true) {
    case totalRemaining > 0:
      return {
        icon: WarningIcon,
        message: `Total remaining quantity on order: ${totalRemaining} ${assetUnit}. You can add more replenishments later.`,
      };
    case totalRemaining < 0:
      return {
        icon: WarningIcon,
        message:
          'You received more than you ordered. This is OK - no further action is required.',
      };
    case totalRemaining === 0:
      return {
        icon: CheckCircleIcon,
        message: 'You have received the full quantity.',
      };
    default:
      return null;
  }
};

export const getSDDOTReplenishmentSubtextOptions = (
  totalOrdered: number,
  totalReceived: number = 0,
  newReceived: number,
  assetUnit: string,
): { icon: IconComponent; message: string } =>
  R.isNil(newReceived) || R.isNil(totalOrdered)
    ? null
    : getReplenishmentSubtextOptions(
        totalOrdered - totalReceived - newReceived,
        assetUnit,
      );

export const getAttributeFilterByName = (
  asset: AssetDetail,
  attributeName: string,
) => {
  if (isNilOrEmpty(asset)) {
    return null;
  }

  const attribute = R.pathOr([], ['attributeGroups'], asset)
    .reduce((acc, group) => [...acc, ...group.attributes], [])
    .find(item => item?.name === attributeName);

  return {
    [`attributes.${attribute?.id}`]: [attribute?.value],
  };
};

export const getApprovalKeyword = (status: AssetRequestStatus): string => {
  const keywords = {
    [AssetRequestStatus.CANCELED]: 'Cancel',
    [AssetRequestStatus.REJECTED]: 'Reject',
    [AssetRequestStatus.FULL_APPROVAL]: 'Approve',
    [AssetRequestStatus.PARTIAL_APPROVAL]: 'Approve',
    [AssetRequestStatus.PENDING_APPROVAL]: 'Approve',
    [AssetRequestStatus.APPROVED]: 'Approve',
  };

  return keywords[status] || '';
};

export const ARDOT_ATTRIBUTES = [
  ARDOTAttribute.TAG_NUMBER,
  ARDOTAttribute.DESCRIPTION,
  ARDOTAttribute.OPERATOR_NAME,
  ARDOTAttribute.COMMUTE_STATUS,
  ARDOTAttribute.MONITORED_STATUS,
];

export const ARDOT_CREATE_ATTRIBUTES = [
  ARDOTAttribute.OPERATOR_NAME,
  ARDOTAttribute.COMMUTE_STATUS,
  ARDOTAttribute.MONITORED_STATUS,
];

export const ardotAttributeDictionary = (asset: AssetDetail) => {
  const attributes = asset?.attributeGroups.reduce((acc, group) => {
    return [...acc, ...group.attributes];
  }, []);

  return attributes.reduce((acc, attribute) => {
    return ARDOT_ATTRIBUTES.includes(attribute?.name as ARDOTAttribute)
      ? { ...acc, [attribute?.name]: attribute }
      : acc;
  }, {});
};

export const getARDOTInitialAttributes = (
  asset: AssetDetail,
): AssetRequestAttribute[] => {
  const attributesDictionary = ardotAttributeDictionary(asset);

  return ARDOT_CREATE_ATTRIBUTES.map((attributeName: ARDOTAttribute) => {
    const attribute = attributesDictionary[attributeName];

    return {
      id: attribute?.id,
      name: attribute?.name,
      oldValue: attribute?.value,
      newValue: attribute?.value,
    };
  });
};

export const getCategoryPathString = (asset: AssetDetail): string => {
  const pathValues = R.pathOr([], ['categories'], asset).map(
    category => category.name,
  );
  return pathValues.join(' / ');
};

export const getInitialCreateInputs = (
  type: AssetRequestType,
  asset: AssetDetail,
): AssetRequestCreateInput => {
  switch (assetRequestClient) {
    case Client.SDDOT:
      return {
        type,
        assetId: asset?.id,
      };
    case Client.ARDOT:
      return {
        type,
        assetId: asset?.id,
        categoryId: asset?.categoryId,
        categoryPath: getCategoryPathString(asset),
        attributes: getARDOTInitialAttributes(asset),
      };
    case Client.ADOT:
      return {
        type,
        assetId: asset?.id,
      };
    default:
      return { type: null, assetId: '' };
  }
};

export const cleanCreateInputs = (
  input: AssetRequestCreateInput,
): AssetRequestCreateInput => {
  const attributes = R.pathOr([], ['attributes'], input);

  const cleaned = {
    ...input,
    attributes: attributes?.filter(
      attribute => attribute?.newValue !== attribute?.oldValue,
    ),
  };

  return assetRequestClient === Client.ADOT
    ? toADOTBaseCreateInputs(cleaned)
    : cleaned;
};

export const getConfirmationTitle = (approvalKeyword: string): string => {
  switch (assetRequestClient) {
    case Client.SDDOT:
      return `${approvalKeyword} Request: Inventory Transfer?`;
    case Client.ARDOT:
      return `${approvalKeyword} 118 Process Request?`;
    case Client.ADOT:
      return `${approvalKeyword} Request: Inventory Transfer?`;
    default:
      return '';
  }
};
